<template>
  <Teleport to="body">
    <transition name="fade">
      <div
        v-if="isOpen"
        class="fixed inset-0 z-40 bg-[#0A0D12]/70 backdrop-blur-sm transition-opacity"
        @click="$emit('close')"
      ></div>
    </transition>

    <transition name="slide">
      <div
        v-if="isOpen"
        class="fixed inset-y-0 right-0 z-50 w-full max-w-[640px] bg-white shadow-2xl sm:rounded-l-xl flex flex-col overflow-hidden"
      >
        <div class="relative flex-shrink-0">
          <button
            @click="$emit('close')"
            class="absolute right-6 top-6 p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors z-10"
          >
            <CloseIcon></CloseIcon>
          </button>

          <div class="p-8 bg-gray-25 flex flex-col gap-6">
            <div class="flex flex-wrap items-start gap-4 pr-12">
              <span
                class="px-2.5 py-0.5 bg-white border border-gray-200 text-gray-700 text-sm rounded-md"
              >
                {{ organisation.organisation_type }}
              </span>
            </div>

            <div class="flex flex-col sm:flex-row gap-6 items-start">
              <div
                class="w-[120px] h-[120px] flex-shrink-0 bg-white border border-gray-200 rounded-lg flex items-center justify-center overflow-hidden"
              >
                <img
                  v-if="organisation.logo_url"
                  :src="organisation.logo_url"
                  alt="Logo"
                  class="object-cover w-full h-full"
                />
                <span v-else class="text-gray-400 text-xs">
                  <Translation name="modal.no_logo" default="No Logo" />
                </span>
              </div>

              <div class="flex flex-col gap-3">
                <h2 class="text-gray-900 text-3xl font-semibold leading-tight">
                  {{ organisation.name }}
                </h2>
                <div class="flex gap-4">
                  <a
                    v-if="organisation.website"
                    :href="organisation.website"
                    target="_blank"
                    class="inline-flex items-center gap-2 px-3 py-2 bg-brand-25 text-brand-700 text-sm font-semibold rounded-lg hover:bg-teal-100 transition-colors w-fit"
                  >
                    <Translation name="modal.visit_website" default="Visit website" />
                    <ArrowLinkIcon></ArrowLinkIcon>
                  </a>

                  <a
                    v-if="organisation.email"
                    :href="`mailto:${organisation.email}`"
                    target="_blank"
                    class="inline-flex items-center gap-2 px-3 py-2 bg-brand-25 text-brand-700 text-sm font-semibold rounded-lg hover:bg-teal-100 transition-colors w-fit"
                  >
                    <MailIcon />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="flex-1 overflow-y-auto">
          <div class="p-8 flex flex-col gap-8">
            <div v-if="organisation.description" class="flex flex-col gap-3">
              <h3 class="text-gray-900 text-xl font-semibold">
                <Translation name="modal.description" default="Description" />
              </h3>
              <p class="text-[#535862] text-base leading-relaxed">
                {{ organisation.description }}
              </p>
            </div>

            <div
              v-if="organisation.countries?.length"
              class="flex flex-col gap-3"
            >
              <h3 class="text-gray-900 text-xl font-semibold">
                <Translation name="modal.countries" default="Countries" />
              </h3>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="country in organisation.countries"
                  :key="country"
                  class="px-3 py-1 bg-[#FAFAFA] border border-gray-100 text-gray-700 text-sm rounded-full flex items-center gap-2"
                >
                  📍 {{ country }}
                </span>
              </div>
            </div>

            <div
              v-if="organisation.sectors?.length"
              class="flex flex-col gap-3"
            >
              <h3 class="text-gray-900 text-xl font-semibold">
                <Translation name="modal.sectors" default="Sectors" />
              </h3>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="sector in organisation.sectors"
                  :key="sector"
                  class="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-sm rounded-lg"
                >
                  {{ sector }}
                </span>
              </div>
            </div>

            <div v-if="organisation.initiatives?.length" class="flex flex-col gap-3">
              <h3 class="text-gray-900 text-xl font-semibold">
                <Translation name="modal.initiatives" default="Initiatives" />
              </h3>
              <div class="flex flex-wrap gap-2">
                <template v-for="initiative in organisation.initiatives" :key="initiative.name">
                  <a
                    v-if="initiative.link"
                    :href="initiative.link"
                    target="_blank"
                    rel="noopener noreferrer"
                    class="px-3 py-1.5 bg-white border border-gray-200 text-brand-700 text-sm rounded-lg inline-flex items-center gap-2 underline hover:bg-brand-25"
                  >
                    {{ initiative.name }}
                    <ArrowLinkIcon />
                  </a>
                  <span v-else class="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-sm rounded-lg">
                    {{ initiative.name }}
                  </span>
                </template>
              </div>
            </div>

            <div
              v-if="organisation.aspects?.length"
              class="flex flex-col gap-3"
            >
              <h3 class="text-gray-900 text-xl font-semibold">
                <Translation name="modal.working_in" default="Working in" />
              </h3>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="aspect in organisation.aspects"
                  :key="aspect"
                  class="px-3 py-1.5 bg-white border border-gray-200 text-gray-700 text-sm rounded-lg"
                >
                  {{ aspect }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div
          class="p-6 bg-white border-t border-gray-100 shadow-[0_-13px_8px_rgba(0,0,0,0.04)] flex justify-end items-center gap-3 flex-shrink-0"
        >
          <button
            @click="$emit('close')"
            class="px-4 py-2 bg-brand-25 text-brand-700 font-semibold rounded-lg hover:bg-teal-100 transition-colors"
          >
            <Translation name="modal.close" default="Close" />
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup>
import { watch } from "vue";
import CloseIcon from "../icons/CloseIcon.vue";
import ArrowLinkIcon from "../icons/ArrowLink.vue";
import MailIcon from "../icons/MailIcon.vue";

const props = defineProps({
  organisation: {
    type: Object,
    required: true,
  },
  isOpen: {
    type: Boolean,
    required: true,
  },
});

const emit = defineEmits(["close"]);

watch(
  () => props.isOpen,
  (newVal) => {
    if (newVal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
  }
);
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.slide-enter-active,
.slide-leave-active {
  transition: transform 0.4s cubic-bezier(0.25, 1, 0.5, 1);
}
.slide-enter-from,
.slide-leave-to {
  transform: translateX(100%);
}
</style>
